import { XorOfUnions } from "@redotech/util/type";
import { z } from "zod";
import { CheckoutAbTestMetricToOptimize } from "../checkouts/checkout-ab-test-builder";
import {
  CustomerTagsSegmentSchema,
  DeliveryDistanceMilesSegmentSchema,
  DeliveryLocationSegmentSchema,
  OrderCountSegmentSchema,
  ProductSkuSegmentSchema,
  ProductTagsSegmentSchema,
  ProductVendorSegmentSchema,
} from "../checkouts/checkout-tree";
import { CheckoutOptimizationWidgetType } from "../checkouts/widget";
import { zExt } from "../common/zod-util";
import {
  AmazonServices,
  Carriers,
  FedexServices,
  UpsServices,
  UspsServices,
} from "../fulfillments/fulfillment-carriers-and-services";
import { SortingCriteria } from "../fulfillments/fulfillment-group";
import { FulfillmentOrderAddressWithIdSchema } from "../fulfillments/fulfillment-order-address";
import { Hazmat } from "../fulfillments/hazmat";
import { ShopifyFulfillmentHoldReason } from "../fulfillments/shopify-fulfillment-schemas";
import { CustomerAttributeDataStructure } from "../marketing/segments/segment-data-structures";
import {
  afterNowRelativeExactTimeframeSchema,
  beforeNowRelativeExactTimeframeSchema,
  todayTimeframeSchema,
} from "../marketing/segments/segment-timeframe-zod-schema";
import {
  ConjunctionMode,
  CustomerCharacteristicType,
} from "../marketing/segments/segment-types";
import { WhereConditionDataType } from "../marketing/segments/segment-where-condition";
import { SpecialRateEligibility } from "../outbound-labels/outbound-labels";
import { ParcelSchema } from "../outbound-labels/parcel";
import { WeightSchema } from "../outbound-labels/util";
import { TagSchema } from "../tag";
import { TimeUnit } from "../time";
import { supportedWaitForEventTypes } from "./common";
import { numberComparisonOperators } from "./expressions/number-expression";
import { textMatchOperators } from "./expressions/text-expression";
import { AutoHoldType, OmsActionType, OmsSettableFields } from "./oms/actions";
import {
  ListToListMatchType,
  OmsConditionType,
  StringToListMatchType,
} from "./oms/conditions";
import { SchemaType } from "./schemas/schemas";
import {
  ConditionDataSource,
  StepType,
  UpsellConditionSource,
  WaitTimeUnit,
} from "./steps";
import {
  categories,
  CheckoutOptimizationTriggerKey,
  MarketingTriggerKey,
  OmsTriggerKey,
  OrderTrackingTriggerKey,
  ReturnTrackingTriggerKey,
  ReviewsTriggerKey,
  TriggerKey,
} from "./triggers";

const baseStepSchema = z.object({
  customTitle: z.string().optional(),
  type: z.nativeEnum(StepType),
  id: z.string(),
});

const baseOmsActionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.OMS_ACTION),
  actionType: z.nativeEnum(OmsActionType),
  field: z.nativeEnum(OmsSettableFields),
  nextId: z.string(),
});

// Probably should just convert categories to an enum
const orderTrackingCategory: (typeof categories)[number] = "Order tracking";
const returnTrackingCategory: (typeof categories)[number] = "Return tracking";
const marketingCategory: (typeof categories)[number] = "Marketing";
const reviewsCategory: (typeof categories)[number] = "Reviews";
const omsCategory: (typeof categories)[number] = "Oms";
const checkoutOptimizationCategory: (typeof categories)[number] =
  "Checkout optimization";
const upsellCategory: (typeof categories)[number] = "Upsell";

const baseTriggerStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.TRIGGER),
  schemaType: z.nativeEnum(SchemaType),
  category: z.enum(categories),
  nextId: z.string(),
});

// Define specific trigger step schemas for each trigger type
export const orderTrackingTriggerStepSchema = baseTriggerStepSchema.extend({
  category: z.literal(orderTrackingCategory),
  key: z.nativeEnum(OrderTrackingTriggerKey),
});

export const returnTrackingTriggerStepSchema = baseTriggerStepSchema.extend({
  category: z.literal(returnTrackingCategory),
  key: z.nativeEnum(ReturnTrackingTriggerKey),
});

const baseMarketingTriggerStepSchema = baseTriggerStepSchema.extend({
  category: z.literal(marketingCategory),
});
// Break down marketing triggers into individual schemas
export const emailSignupTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.EMAIL_SIGNUP),
  });

export const emailSignupShopifyTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY),
  });

export const smsSignupTriggerStepSchema = baseMarketingTriggerStepSchema.extend(
  { key: z.literal(MarketingTriggerKey.SMS_SIGNUP) },
);

export const smsConfirmedTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.SMS_CONFIRMED),
  });

export const marketingCampaignTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.MARKETING_CAMPAIGN),
  });

export const cartAbandonedTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CART_ABANDONED),
  });

export const browseAbandonedTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.BROWSE_ABANDONED),
  });

export const checkoutAbandonedTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CHECKOUT_ABANDONED),
  });

export const backInStockTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.BACK_IN_STOCK),
  });

export const customerGroupEnteredTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CUSTOMER_GROUP_ENTERED),
  });

export const customerGroupExitedTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.CUSTOMER_GROUP_EXITED),
  });

export const warrantyRegistrationTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.WARRANTY_REGISTRATION),
  });

export const marketingLowInventoryTriggerStepSchema =
  baseMarketingTriggerStepSchema.extend({
    key: z.literal(MarketingTriggerKey.LOW_INVENTORY),
  });

const marketingDateTriggerDimensions = [
  CustomerCharacteristicType.BIRTHDAY,
] as const;
export type MarketingDateTriggerDimension =
  (typeof marketingDateTriggerDimensions)[number];
/** Only annual date characteristics on customers are supported for
 * marketing date triggers. This function expression enforces that. */
() => {
  type dimensionsExceptDateAnnual = Exclude<
    // Get the data types for the dimensions
    (typeof CustomerAttributeDataStructure)["fields"][(typeof marketingDateTriggerDimensions)[number]]["dataType"],
    // Remove the date annual data type (should be the only one)
    WhereConditionDataType.DATE_ANNUAL
  >;
  const b: dimensionsExceptDateAnnual =
    "" as unknown as dimensionsExceptDateAnnual;
  b satisfies never;
};
const marketingDateTriggerStepSchema = baseMarketingTriggerStepSchema.extend({
  key: z.literal(MarketingTriggerKey.DATE),
  triggerSpecificFields: z.object({
    dimension: z.nativeEnum(CustomerCharacteristicType),
    comparison: z.discriminatedUnion("type", [
      beforeNowRelativeExactTimeframeSchema,
      afterNowRelativeExactTimeframeSchema,
      todayTimeframeSchema,
    ]),
  }),
});
export type MarketingDateTriggerStep = z.infer<
  typeof marketingDateTriggerStepSchema
>;

export const marketingTriggerStepSchemas = z.discriminatedUnion("key", [
  emailSignupTriggerStepSchema,
  emailSignupShopifyTriggerStepSchema,
  smsSignupTriggerStepSchema,
  smsConfirmedTriggerStepSchema,
  marketingCampaignTriggerStepSchema,
  cartAbandonedTriggerStepSchema,
  browseAbandonedTriggerStepSchema,
  checkoutAbandonedTriggerStepSchema,
  backInStockTriggerStepSchema,
  customerGroupEnteredTriggerStepSchema,
  customerGroupExitedTriggerStepSchema,
  warrantyRegistrationTriggerStepSchema,
  marketingLowInventoryTriggerStepSchema,
  marketingDateTriggerStepSchema,
]);

export const reviewsTriggerStepSchema = baseTriggerStepSchema.extend({
  category: z.literal(reviewsCategory),
  key: z.nativeEnum(ReviewsTriggerKey),
});

export const omsTriggerStepSchema = baseTriggerStepSchema.extend({
  category: z.literal(omsCategory),
  key: z.nativeEnum(OmsTriggerKey),
});

export const checkoutOptimizationTriggerStepSchema =
  baseTriggerStepSchema.extend({
    category: z.literal(checkoutOptimizationCategory),
    key: z.nativeEnum(CheckoutOptimizationTriggerKey),
  });

const upsellTriggerStepSchema = baseTriggerStepSchema.extend({
  category: z.literal(upsellCategory),
  key: z.literal(OrderTrackingTriggerKey.POST_PURCHASE_UPSELL),
});

// TODO: Once zod 4 is released, switch to discriminated union
export const triggerStepSchema = z.union([
  orderTrackingTriggerStepSchema,
  returnTrackingTriggerStepSchema,
  marketingTriggerStepSchemas,
  reviewsTriggerStepSchema,
  omsTriggerStepSchema,
  checkoutOptimizationTriggerStepSchema,
  upsellTriggerStepSchema,
]);

const waitStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.WAIT),
  numDays: z.number(),
  numSeconds: z.number().optional(),
  timeUnit: z.nativeEnum(WaitTimeUnit).default(WaitTimeUnit.DAYS).optional(),
  nextId: z.string(),
});

const waitForEventStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.WAIT_FOR_EVENT),
  eventType: z.enum(supportedWaitForEventTypes),
  nextId: z.string(),
});

const sendEmailStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.SEND_EMAIL),
  templateId: z.string(),
  emailAddressFieldName: z.string(),
  recipientNameFieldName: z.string(),
  nextId: z.string().optional(),
});

const sendSmsStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.SEND_SMS),
  templateId: z.string(),
  phoneNumberFieldName: z.string(),
  recipientNameFieldName: z.string(),
  nextId: z.string().optional(),
});

const numberComparisonExpressionSchema = z.object({
  type: z.literal("number_comparison"),
  field: z.union([z.string(), z.number()]),
  operator: z.enum(numberComparisonOperators),
  comparisonValue: z.number(),
});

const textMatchExpressionSchema = z.object({
  type: z.literal("text_match"),
  field: z.union([z.string(), z.number()]),
  operator: z.enum(["startsWith", "endsWith", "includes", "equals"]),
  matchValues: z.array(z.string()),
});

export const schemaBooleanExpressionSchema = z.lazy(() =>
  z.union([
    numberComparisonExpressionSchema,
    textMatchExpressionSchema,
    arrayPredicateExpressionSchema,
    booleanEvaluationExpressionSchema,
  ]),
);

export type SchemaBooleanExpressionType = z.infer<
  typeof schemaBooleanExpressionSchema
>;

const arrayPredicateExpressionSchema = z.object({
  type: z.literal("array_predicate"),
  field: z.union([z.string(), z.number()]),
  operator: z.enum(["some", "every"]),
  condition: z.lazy(() => schemaBooleanExpressionSchema),
}) as z.ZodType<{
  type: "array_predicate";
  field: string | number;
  operator: "some" | "every";
  condition: SchemaBooleanExpressionType;
}>;

const booleanEvaluationExpressionSchema = z.object({
  type: z.literal("boolean_evaluation"),
  field: z.union([z.string(), z.number()]),
});

const conditionExpressionSchema = z.discriminatedUnion("dataSource", [
  z.object({
    dataSource: z.literal(ConditionDataSource.TriggerData),
    schemaBooleanExpression: schemaBooleanExpressionSchema,
  }),
  z.object({
    dataSource: z.literal(ConditionDataSource.ExistingSegment),
    segmentIds: z.array(z.string()),
  }),
  z.object({
    dataSource: z.literal(ConditionDataSource.InlineSegment),
    inlineSegment: z.object({
      mode: z.nativeEnum(ConjunctionMode),
      conditions: z.array(z.any()), //TODO: Replace with queryConditionSchema (requires migration)
    }),
  }),
  z.object({ dataSource: z.literal(ConditionDataSource.OfferResponse) }),
]);

const conditionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.CONDITION),
  expression: conditionExpressionSchema,
  nextTrueId: z.string(),
  nextFalseId: z.string(),
});

const upsellConditionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.UPSELL_CONDITION),
  expression: z
    .object({
      source: z.nativeEnum(UpsellConditionSource),
      products: z
        .array(z.object({ id: z.string(), name: z.string() }))
        .optional(),
      collection: z.string().optional(),
    })
    .optional(),
  isDefault: z.boolean().optional(),
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
  name: z.string().optional(),
});

const upsellStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.UPSELL),
  name: z.string().optional(),
  upsellId: z.string(),
  nextId: z.string().optional(),
});

const downsellStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.DOWNSELL),
  name: z.string().optional(),
  downsellId: z.string(),
  nextId: z.string().optional(),
});

const doNothingStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.DO_NOTHING),
});

/* Checkout optimization */
const rcoSplitStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.RCO_SPLIT),
  name: z.string(),
  hypothesis: z.string(),
  splitId: zExt.objectId(),
  metricToOptimize: z.nativeEnum(CheckoutAbTestMetricToOptimize),
  treatments: z.array(z.object({ nextId: z.string(), name: z.string() })),
});

const checkoutExperienceStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.CHECKOUT_EXPERIENCE),
  name: z.string(),
  widgets: z.array(z.nativeEnum(CheckoutOptimizationWidgetType)),
  checkoutExperienceId: zExt.objectId(),
  nextId: z.string().optional(), // Should never be set
});

/* Segmentation */
export const rcoDeliveryDistanceMilesSegmentSchema =
  DeliveryDistanceMilesSegmentSchema;
export type RcoDeliveryDistanceMilesSegmentSchema = z.infer<
  typeof rcoDeliveryDistanceMilesSegmentSchema
>;
export const rcoOrderCountSegmentSchema = OrderCountSegmentSchema;
export type RcoOrderCountSegmentSchema = z.infer<
  typeof rcoOrderCountSegmentSchema
>;
export const rcoDeliveryLocationSegmentSchema = DeliveryLocationSegmentSchema;
export type RcoDeliveryLocationSegmentSchema = z.infer<
  typeof rcoDeliveryLocationSegmentSchema
>;
export const rcoCustomerTagsSegmentSchema = CustomerTagsSegmentSchema;
export type RcoCustomerTagsSegmentSchema = z.infer<
  typeof rcoCustomerTagsSegmentSchema
>;
export const rcoProductTagsSegmentSchema = ProductTagsSegmentSchema;
export type RcoProductTagsSegmentSchema = z.infer<
  typeof rcoProductTagsSegmentSchema
>;
export const rcoProductVendorSegmentSchema = ProductVendorSegmentSchema;
export type RcoProductVendorSegmentSchema = z.infer<
  typeof rcoProductVendorSegmentSchema
>;
export const rcoProductSkuSegmentSchema = ProductSkuSegmentSchema;
export type RcoProductSkuSegmentSchema = z.infer<
  typeof rcoProductSkuSegmentSchema
>;

const rcoSegmentSchema = z.discriminatedUnion("segmentableCharacteristic", [
  rcoDeliveryDistanceMilesSegmentSchema,
  rcoOrderCountSegmentSchema,
  rcoDeliveryLocationSegmentSchema,
  rcoCustomerTagsSegmentSchema,
  rcoProductTagsSegmentSchema,
  rcoProductVendorSegmentSchema,
  rcoProductSkuSegmentSchema,
]);

const rcoSegmentStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.RCO_SEGMENT),
  matchingNextId: z.string(),
  nonMatchingNextId: z.string(),
  segment: rcoSegmentSchema,
});
/* End of segmentation */
/* End of checkout optimization */

const setShippingServiceActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SHIPPING_SERVICE),
  actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE),
  newShippingService: z.union([
    z.nativeEnum(FedexServices),
    z.nativeEnum(UpsServices),
    z.nativeEnum(UspsServices),
    z.nativeEnum(AmazonServices),
  ]),
  newShippingCarrier: z.nativeEnum(Carriers),
});

const setParcelActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PARCEL),
  actionType: z.literal(OmsActionType.SET_PARCEL),
  parcel: ParcelSchema,
});

const setShippingServiceCheapestActionStepSchema =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.SHIPPING_SERVICE_CHEAPEST),
    actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE_CHEAPEST),
    servicesToChooseFrom: z.array(
      z.object({
        service: z.union([
          z.nativeEnum(FedexServices),
          z.nativeEnum(UpsServices),
          z.nativeEnum(UspsServices),
          z.nativeEnum(AmazonServices),
        ]),
        carrier: z.nativeEnum(Carriers),
      }),
    ),
    selectionCriteria: z.literal(SortingCriteria.CHEAPEST),
  });

const setShippingServiceFastestActionStepSchema =
  baseOmsActionStepSchema.extend({
    field: z.literal(OmsSettableFields.SHIPPING_SERVICE_FASTEST),
    actionType: z.literal(OmsActionType.SET_SHIPPING_SERVICE_FASTEST),
    servicesToChooseFrom: z.array(
      z.object({
        service: z.union([
          z.nativeEnum(FedexServices),
          z.nativeEnum(UpsServices),
          z.nativeEnum(UspsServices),
          z.nativeEnum(AmazonServices),
        ]),
        carrier: z.nativeEnum(Carriers),
      }),
    ),
    selectionCriteria: z.literal(SortingCriteria.FASTEST),
  });

const setPackingSlipTemplateActionStep = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PACKING_SLIP_TEMPLATE),
  actionType: z.literal(OmsActionType.SET_PACKING_SLIP_TEMPLATE),
  packingSlipName: z.string(),
  packingSlipTemplateId: z.string(),
});

const addTagActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.TAG),
  actionType: z.literal(OmsActionType.ADD_TAG),
  tags: z.array(TagSchema),
});

const placeHoldActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PLACE_HOLD),
  actionType: z.literal(OmsActionType.PLACE_HOLD),
  holdOptions: z.object({
    holdType: z.literal(AutoHoldType.Duration),
    duration: z.number(),
    timeUnit: z.nativeEnum(TimeUnit),
    holdReason: z.nativeEnum(ShopifyFulfillmentHoldReason),
    reasonNotes: z.string().nullish(),
  }),
});

const placeHoldUntilActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.PLACE_HOLD_UNTIL),
  actionType: z.literal(OmsActionType.PLACE_HOLD_UNTIL),
  holdOptions: z.object({
    holdType: z.literal(AutoHoldType.Date),
    date: z.coerce.date(),
    holdReason: z.nativeEnum(ShopifyFulfillmentHoldReason),
    reasonNotes: z.string().nullish(),
  }),
});

const setWeightActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SET_WEIGHT),
  actionType: z.literal(OmsActionType.SET_WEIGHT),
  weight: WeightSchema,
});

const setAssignedUserActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.ASSIGNED_USER),
  actionType: z.literal(OmsActionType.SET_ASSIGNED_USER),
  userId: z.string(),
});

const setDutiesPayerActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.DUTIES_PAYER),
  actionType: z.literal(OmsActionType.SET_DUTIES_PAYER),
  ddp: z.boolean(),
});

const setTaxIdentifiersActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.TAX_IDENTIFIERS),
  actionType: z.literal(OmsActionType.SET_TAX_IDENTIFIERS),
  taxIdentifierIds: z.array(z.string()),
});

const setReturnAddressActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.RETURN_ADDRESS),
  actionType: z.literal(OmsActionType.SET_RETURN_ADDRESS),
  address: FulfillmentOrderAddressWithIdSchema,
});

const shippingOptionsSchema = z.object({
  signatureRequired: z.boolean().nullish(),
  adultSignatureRequired: z.boolean().nullish(),
  includeInsurance: z.boolean().nullish(),
  specialRateEligibility: z.nativeEnum(SpecialRateEligibility).nullish(),
  hazmat: z.nativeEnum(Hazmat).nullish(),
});

const setShippingOptionsActionStepSchema = baseOmsActionStepSchema.extend({
  field: z.literal(OmsSettableFields.SHIPPING_OPTIONS),
  actionType: z.literal(OmsActionType.SET_SHIPPING_OPTIONS),
  shippingOptions: shippingOptionsSchema,
});

const baseOmsConditionStepSchema = baseStepSchema.extend({
  type: z.literal(StepType.OMS_CONDITION),
  conditionType: z.nativeEnum(OmsConditionType),
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
});

const omsOrderTagConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.ORDER_TAG),
  matchType: z.nativeEnum(ListToListMatchType).optional(),
  matchValues: z.array(z.string()),
  requireExactMatch: z.boolean().optional(),
});

const omsSelectedShippingConditionStepSchema =
  baseOmsConditionStepSchema.extend({
    conditionType: z.literal(OmsConditionType.SELECTED_SHIPPING),
    matchType: z.nativeEnum(ListToListMatchType).optional(),
    matchValues: z.array(z.string()),
    requireExactMatch: z.boolean().optional(),
  });

const omsSkuConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.SKU),
  matchType: z.nativeEnum(ListToListMatchType).optional(),
  matchValues: z.array(z.string()),
  requireExactMatch: z.boolean().optional(),
});

const omsProductNameConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.PRODUCT_NAME),
  matchType: z.nativeEnum(ListToListMatchType).optional(),
  matchValues: z.array(z.string()),
  requireExactMatch: z.boolean().optional(),
});

const omsItemQuantityConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.ITEM_QUANTITY),
  matchType: z.enum(numberComparisonOperators).optional(),
  matchValue: z.number(),
});

const omsPackageWeightOzConditionStepSchema = baseOmsConditionStepSchema.extend(
  {
    conditionType: z.literal(OmsConditionType.PACKAGE_WEIGHT_OZ),
    matchType: z.enum(numberComparisonOperators).optional(),
    matchValue: z.number(),
  },
);

const omsTotalValueConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.TOTAL_VALUE),
  matchType: z.enum(numberComparisonOperators).optional(),
  matchValue: z.number(),
});

const omsDestinationCountryConditionStepSchema =
  baseOmsConditionStepSchema.extend({
    conditionType: z.literal(OmsConditionType.DESTINATION_COUNTRY),
    matchType: z.nativeEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });

const omsDestinationProvinceConditionStepSchema =
  baseOmsConditionStepSchema.extend({
    conditionType: z.literal(OmsConditionType.DESTINATION_PROVINCE),
    matchType: z.nativeEnum(StringToListMatchType).optional(),
    matchValues: z.array(z.string()),
  });

const omsDeliveryMethodConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.DELIVERY_METHOD),
  matchType: z.nativeEnum(StringToListMatchType).optional(),
  matchValues: z.array(z.string()),
});

const omsStreetAddressConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.STREET_ADDRESS),
  matchType: z.enum(textMatchOperators).optional(),
  matchValue: z.string(),
});

const omsSalesChannelConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.SALES_CHANNEL),
  matchType: z.nativeEnum(StringToListMatchType).optional(),
  matchValues: z.array(z.string()),
});

const omsProductTagConditionStepSchema = baseOmsConditionStepSchema.extend({
  conditionType: z.literal(OmsConditionType.PRODUCT_TAG),
  matchType: z.nativeEnum(ListToListMatchType).optional(),
  matchValues: z.array(z.string()),
  requireExactMatch: z.boolean().optional(),
});

export const omsActionStepSchema = z.discriminatedUnion("actionType", [
  setShippingServiceActionStepSchema,
  setParcelActionStepSchema,
  setShippingServiceCheapestActionStepSchema,
  setShippingServiceFastestActionStepSchema,
  setPackingSlipTemplateActionStep,
  addTagActionStepSchema,
  setShippingOptionsActionStepSchema,
  placeHoldActionStepSchema,
  placeHoldUntilActionStepSchema,
  setWeightActionStepSchema,
  setAssignedUserActionStepSchema,
  setDutiesPayerActionStepSchema,
  setTaxIdentifiersActionStepSchema,
  setReturnAddressActionStepSchema,
]);

export const omsConditionStepSchema = z.discriminatedUnion("conditionType", [
  omsOrderTagConditionStepSchema,
  omsSelectedShippingConditionStepSchema,
  omsSkuConditionStepSchema,
  omsProductNameConditionStepSchema,
  omsItemQuantityConditionStepSchema,
  omsPackageWeightOzConditionStepSchema,
  omsTotalValueConditionStepSchema,
  omsDestinationCountryConditionStepSchema,
  omsDestinationProvinceConditionStepSchema,
  omsDeliveryMethodConditionStepSchema,
  omsStreetAddressConditionStepSchema,
  omsSalesChannelConditionStepSchema,
  omsProductTagConditionStepSchema,
]);

// TODO: Once zod 4 is released, switch back to discriminated union
export const upsellFlowStepSchema = z.union([
  upsellConditionStepSchema,
  upsellStepSchema,
  downsellStepSchema,
  doNothingStepSchema,
  triggerStepSchema,
]);

export const omsStepSchema = z.union([
  omsConditionStepSchema,
  omsActionStepSchema,
  doNothingStepSchema,
]);

// TODO: Once zod 4 is released, switch back to discriminated union
export const marketingStepSchema = z.union([
  triggerStepSchema,
  waitStepSchema,
  waitForEventStepSchema,
  sendEmailStepSchema,
  sendSmsStepSchema,
  conditionStepSchema,
  doNothingStepSchema,
]);

export const checkoutOptimizationStepSchema = z.discriminatedUnion("type", [
  rcoSplitStepSchema,
  checkoutExperienceStepSchema,
  rcoSegmentStepSchema,
]);

export const stepSchema = z.union([
  omsStepSchema,
  marketingStepSchema,
  upsellFlowStepSchema,
  checkoutOptimizationStepSchema,
]);

export const advancedFlowSchema = z.object({
  team: zExt.objectId(),
  name: z.string().optional(),
  enabled: z.boolean(),
  steps: z
    .array(stepSchema)
    .refine(
      (steps) => steps.filter((s) => s.type === StepType.TRIGGER).length === 1,
      "Flow must have exactly one trigger step",
    ),
  schemaType: z.nativeEnum(SchemaType),
  category: z.enum(categories),
  createdByUserId: zExt.objectId().optional(),
  createdAt: z.date().optional(),
  index: z.number().optional(),
  versionGroupId: zExt.objectId(),
  publishedAt: z.date().optional(),
  _id: zExt.objectId(),
  updatedAt: z.date().optional(),
  __v: z.number().optional(),
});

export type TriggerStep = z.infer<typeof triggerStepSchema>;
export type ConditionStep = z.infer<typeof conditionStepSchema>;
export type SendSmsStep = z.infer<typeof sendSmsStepSchema>;
export type AdvancedFlow = z.infer<typeof advancedFlowSchema>;
export type FlowStep = z.infer<typeof stepSchema>;
export type MarketingStep = z.infer<typeof marketingStepSchema>;
export type UpsellFlowStep = z.infer<typeof upsellFlowStepSchema>;
export type WaitStep = z.infer<typeof waitStepSchema>;
export type WaitForEventStep = z.infer<typeof waitForEventStepSchema>;
export type UpsellStep = z.infer<typeof upsellStepSchema>;
export type DownsellStep = z.infer<typeof downsellStepSchema>;
export type UpsellConditionStep = z.infer<typeof upsellConditionStepSchema>;
export type SendEmailStep = z.infer<typeof sendEmailStepSchema>;
export type DoNothingStep = z.infer<typeof doNothingStepSchema>;

// Checkout optimization
export type RcoSplitStep = z.infer<typeof rcoSplitStepSchema>;
export type CheckoutExperienceStep = z.infer<
  typeof checkoutExperienceStepSchema
>;
export type RcoSegmentStep = z.infer<typeof rcoSegmentStepSchema>;
export type CheckoutOptimizationStep = z.infer<
  typeof checkoutOptimizationStepSchema
>;

export type NewAdvancedFlow = Omit<
  AdvancedFlow,
  "createdAt" | "updatedAt" | "schemaVersion" | "_id" | "versionGroupId"
>;

// For every trigger key, there should be a trigger step schema
// in this file. This function expression enforces that.
() => {
  type a = XorOfUnions<TriggerStep["key"], TriggerKey>;
  const b: a = "" as unknown as a;
  b satisfies never;
};
